import * as React from "react"

/*
 * MARK V — camada compartilhada dos charts. Constantes de paleta (strings
 * CSS-var, theming centralizado em src/styles/charts.css) + hooks pequenos
 * usados pelos componentes: injeção de <style> guardada e hook de largura via
 * ResizeObserver. Porte fiel de mark-v-design-system/components/charts/chartStyles.js.
 */

/** Paleta categórica (ciclada por índice). */
export const CHART_SERIES = [
  "var(--markv-chart-1)",
  "var(--markv-chart-2)",
  "var(--markv-chart-3)",
  "var(--markv-chart-4)",
  "var(--markv-chart-5)",
  "var(--markv-chart-6)",
] as const

/** Rampa sequencial de métrica única, claro -> escuro. */
export const CHART_SEQ = [
  "var(--markv-chart-seq-1)",
  "var(--markv-chart-seq-2)",
  "var(--markv-chart-seq-3)",
  "var(--markv-chart-seq-4)",
  "var(--markv-chart-seq-5)",
] as const

/** Tons dirigidos por significado. */
export const CHART_TONE = {
  primary: "var(--markv-chart-1)",
  positive: "var(--markv-chart-positive)",
  negative: "var(--markv-chart-negative)",
  neutral: "var(--markv-chart-neutral)",
  compare: "var(--markv-chart-compare)",
  highlight: "var(--markv-chart-highlight)",
} as const

export type ChartTone = keyof typeof CHART_TONE

/** Resolve uma cor: keyword de tom -> token, senão pass-through (hex / var). */
export function resolveColor(c: string | undefined, fallback: string): string {
  if (!c) return fallback
  return (CHART_TONE as Record<string, string>)[c] ?? c
}

/** Cor de série por índice, ciclando a paleta categórica. */
export function seriesColor(i: number): string {
  return CHART_SERIES[i % CHART_SERIES.length] as string
}

/** Injeção de <style> guardada por id (dedupe entre instâncias). */
export function injectStyle(id: string, css: string): void {
  if (typeof document === "undefined" || document.getElementById(id)) return
  const el = document.createElement("style")
  el.id = id
  el.textContent = css
  document.head.appendChild(el)
}

/** Mede a largura content-box de um elemento (linhas SVG nítidas, sem escala). */
export function useMeasuredWidth(ref: React.RefObject<HTMLElement | null>): number {
  const [w, setW] = React.useState(0)
  React.useLayoutEffect(() => {
    const node = ref.current
    if (!node || typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect
      if (cr) setW(cr.width)
    })
    ro.observe(node)
    setW(node.getBoundingClientRect().width)
    return () => ro.disconnect()
  }, [ref])
  return w
}

/* Classes base compartilhadas por todo chart (wrapper, eixo, legenda). */
const BASE_CSS = `
.mv-chart{ font-family:var(--markv-font-body); color:var(--markv-text-700); width:100%; }
.mv-chart__head{ display:flex; align-items:baseline; justify-content:space-between; gap:12px; margin-bottom:14px; }
.mv-chart__title{ font-family:var(--markv-font-display); font-weight:var(--markv-weight-bold);
  font-size:var(--markv-text-md); color:var(--markv-text-700); margin:0; }
.mv-chart__sub{ font-size:var(--markv-text-xs); color:var(--markv-text-400); }
.mv-chart__legend{ display:flex; flex-wrap:wrap; gap:6px 16px; margin-top:14px; }
.mv-chart__lg{ display:inline-flex; align-items:center; gap:7px; font-size:var(--markv-text-xs);
  color:var(--markv-text-500); }
.mv-chart__sw{ width:10px; height:10px; border-radius:3px; flex-shrink:0; }
.mv-chart__sw--line{ height:3px; border-radius:2px; width:14px; }
.mv-chart__val{ font-family:var(--markv-font-display); font-weight:var(--markv-weight-bold);
  font-variant-numeric:tabular-nums; color:var(--markv-chart-value); }
.mv-chart__cat{ font-size:var(--markv-text-xs); color:var(--markv-chart-label); text-align:center;
  line-height:1.2; }
.mv-chart__tick{ font-size:var(--markv-text-2xs); color:var(--markv-chart-label);
  font-variant-numeric:tabular-nums; }
`

export function useChartBase(): void {
  React.useEffect(() => injectStyle("mv-chart-base", BASE_CSS), [])
}

export type NumberFormatter = (v: number) => string | number
